<template>
  <div
    class="app-select"
    :class="inputClasses"
  >
    <div class="app-select__container">
      <!-- Label -->
      <label
        v-if="label"
        class="app-select__label"
        :class="isFloating"
        :for="name"
      >
        <span>{{ label }}</span>
      </label>

      <!-- Error -->
      <transition name="fade">
        <span
          v-if="error"
          class="app-select__error"
        >
          {{ error }}
        </span>
      </transition>

      <!-- Select -->
      <select
        class="app-select__field"
        :class="fieldClasses"
        :id="id || name"
        :name="name"
        :value="value"
        @blur="focused = false"
        @focus="focused = true"
        @change="$emit('input', $event.target.value)"
      >
        <option
          v-if="placeholder"
          selected="selected"
          :value="undefined"
        >{{ placeholder }}</option>

        <option
          v-for="(option, index) in options"
          :key="index"
          :value="itemValue ? option[itemValue] : option"
        >{{ itemText ? option[itemText] : option }}</option>
      </select>

      <FontAwesomeIcon
        class="app-select__icon"
        icon="caret-down"
      />
    </div>
  </div>
</template>

<script>
export default {
  // Name
  name: 'app-select',

  // Props
  props: {
    id: String,
    name: String,
    value: String,
    itemText: String,
    itemValue: String,
    placeholder: String,
    options: Array,
    label: String,
    error: String,
    optgroup: Boolean
  },

  data () {
    return {
      focused: false,
      blurred: false
    }
  },

  computed: {
    isFloating () {
      if (!this.value && !this.focused) return false
      return 'floating'
    },

    inputClasses () {
      return {
        'active': this.isFloating,
        // 'horizontal': this.horizontal,
        'focused': this.focused,
        'has-error': this.error,
        'has-label': this.label
      }
    },

    fieldClasses () {
      return {
        'has-error': this.error,
        // 'has-icon': this.icon,
        'has-label': this.label
      }
    }
  }
}
</script>

<style scoped lang="scss">
$input-border: 1px solid #d6d6d6;
$input-color-label: #A7A7A7;
$input-color-background: #F4F4F4;
$input-color-text: #3F3F3F;
$input-color-border: #D6D6D6;
$input-color-divider: #DDD;
$input-color-border-active: #3CBBF5;
$input-color-error: #DF2C21;

.app-select {
  margin-bottom: 15px;
  width: 100%;

  @include breakpoint('tablet-portrait-up') {
    margin-bottom: 25px;
  }

  @include breakpoint('tablet-landscape-up') {
    margin-left: 10px;

    &:first-child {
      margin-left: 0;
    }
  }

  &__container {
    position: relative;
    width: 100%;
  }

  &__label {
    position: absolute;
    top: 0;
    left: 0;
    font-size: 16px;
    font-weight: 400;
    color: $input-color-label;
    pointer-events: none;
    transform-origin: 0 0;
    transform: translate3D(15px, 15px, 0);
    transition: transform 175ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      font-size: 22px;
      transform: translate3D(15px, 26px, 0);
    }

    &.floating {
      transform: translate3D(15px, 5px, 0) scale(0.75);

      @include breakpoint('tablet-portrait-up') {
        transform: translate3D(15px, 12px, 0) scale(0.63);
      }
    }
  }

  &__field {
    height: 55px;
    width: 100%;
    color: $input-color-text;
    background: $input-color-background;
    box-shadow: none;
    border: 1px solid transparent;
    border-top: 1px solid $input-color-border;
    flex-basis: 100%;
    outline: none;
    padding: 10px 0 10px 15px;
    font-size: 18px;
    caret-color: $brand-color-primary;
    -moz-appearance: textfield;
    -webkit-appearance: none;
    border-radius: 4px;
    transition: outline 150ms ease-out,
      box-shadow 150ms ease-out,
      border-color 150ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      border-width: 2px;
      font-size: 22px;
      height: 75px;
    }

    &:focus {
      border-color: $input-color-border-active;
    }

    &.has-label {
      padding-top: 25px;
    }

    &.has-error {
      border-color: $input-color-error;
      box-shadow: 0 4px 0 0 $input-color-error;
    }

    &::-ms-expand {
      display: none;
    }
  }

  &__error {
    position: absolute;
    left: 0px;
    top: 2px;
    color: $theme-error;
    font-size: 9px;
    font-style: italic;

    @include breakpoint('tablet-landscape-up') {
      top: -28px;
      font-size: 11px;
    }

    @include breakpoint('desktop-up') {
      font-size: 14px;
    }
  }

  &__icon {
    position: absolute;
    right: 15px;
    top: 20px;

    @include breakpoint('tablet-portrait-up') {
      right: 20px;
      top: 30px;
    }
  }

  &.has-error .app-select__field {
    border-color:  $theme-error;
    box-shadow: 0 4px 0px 0px $theme-error;
  }

}
</style>
